import React, { createContext, useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { BusinessContext } from './BusinessContext';  // Import BusinessContext
import { useLocation } from 'react-router-dom'; // Add useLocation import

export const ReviewsContext = createContext();

const API_URL = process.env.REACT_APP_API_URL;
const ITEMS_PER_SENTIMENT_PAGE_DEFAULT = 10; // Define page size for comments panel
const REVIEWS_TABLE_PAGE_SIZE = 20; // Default page size for reviews table

// Utility function to calculate date range for standard timeframes
const calculateDateRangeForTimeframe = (timeframe) => {
  if (timeframe === "all") {
    return null; // No date range for "all time"
  }
  
  const days = parseInt(timeframe, 10);
  if (!isNaN(days)) {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - days);
    return { start: startDate, end: endDate };
  }
  
  return null;
};

export const ReviewsProvider = ({ children }) => {
  const location = useLocation(); // Add location hook to detect current page
  const [reviewsData, setReviewsData] = useState([]);
  const [analysisData, setAnalysisData] = useState({}); // Map: { reviewId: [Analysis] }
  const [isReviewsLoading, setIsReviewsLoading] = useState(false); // Start as false, fetch is explicit
  const [isAnalysisLoading, setIsAnalysisLoading] = useState(false); // Start as false
  const [error, setError] = useState(null);
  const [timeframe, setTimeframe] = useState(() => {
    // Only return "custom" if it was truly a custom date range (not calculated from "Last X Days")
    try {
      const savedRange = localStorage.getItem("selectedDateRange");
      const isCustomRange = localStorage.getItem("isCustomDateRange") === "true";
      if (savedRange && isCustomRange) {
        return "custom";
      }
    } catch (e) {
      // Ignore error and fall back to regular timeframe
    }
    return localStorage.getItem("timeframe") || "365";
  });
  const [view, setView] = useState(() => {
    return localStorage.getItem("view") || "Sales";
  });
  // Sort options: 'total', 'positive', 'negative'
  const [sortOption, setSortOption] = useState(() => {
    const savedSortOption = localStorage.getItem("sortOption");
    // Only use saved value if it's valid, otherwise default to "total"
    return (savedSortOption === "total" || savedSortOption === "positive" || savedSortOption === "negative") 
      ? savedSortOption 
      : "total";
  });
  
  // Source filtering: array of selected sources, default to "all"
  const [selectedSources, setSelectedSources] = useState(() => {
    try {
      const savedSources = localStorage.getItem("selectedSources");
      // Handle both old string format and new array format
      if (savedSources) {
        const parsed = JSON.parse(savedSources);
        const initialSources = Array.isArray(parsed) ? parsed : [parsed];
        return initialSources.length > 0 ? initialSources : ["all"];
      }
      return ["all"];
    } catch (e) {
      // If parsing fails, default to "all"
      return ["all"];
    }
  });

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedAttribute, setSelectedAttribute] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDateRange, setSelectedDateRange] = useState(() => {
    try {
      const savedRange = localStorage.getItem("selectedDateRange");
      const isCustomRange = localStorage.getItem("isCustomDateRange") === "true";
      
      // Only restore saved date range if it was truly custom
      if (savedRange && isCustomRange) {
        const parsed = JSON.parse(savedRange);
        return {
          start: new Date(parsed.start),
          end: new Date(parsed.end)
        };
      } else if (savedRange && !isCustomRange) {
        // Clear old calculated ranges from previous version
        localStorage.removeItem("selectedDateRange");
        localStorage.removeItem("isCustomDateRange");
      }
    } catch (e) {
      localStorage.removeItem("selectedDateRange");
      localStorage.removeItem("isCustomDateRange");
    }
    return null;
  }); // { start: Date, end: Date }
  const [selectedReviewId, setSelectedReviewId] = useState(null); // ADDED: State for selected review ID
  
  const { selectedBusinesses } = useContext(BusinessContext);
  const [showSentimentExplanation, setShowSentimentExplanation] = useState(false);
  const [showTopicExplanation, setShowTopicExplanation] = useState(false);
  
  // --- NEW: State for Paginated Reviews Table ---
  const [tableReviews, setTableReviews] = useState([]);
  const [tableReviewsPage, setTableReviewsPage] = useState(1);
  const [tableReviewsHasMore, setTableReviewsHasMore] = useState(true);
  const [isTableReviewsLoading, setIsTableReviewsLoading] = useState(false);
  const [tableReviewsError, setTableReviewsError] = useState(null);
  // --- END NEW: State for Paginated Reviews Table ---
  
  // Calculate effective date range: use custom range if available, otherwise calculate from timeframe
  const effectiveDateRange = useMemo(() => {
    if (timeframe === "custom" && selectedDateRange) {
      return selectedDateRange;
    }
    return calculateDateRangeForTimeframe(timeframe);
  }, [timeframe, selectedDateRange]);
  
  // Function to reset matrix/chart specific filters
  const resetFilters = useCallback(() => { // MODIFIED: Added setSelectedReviewId to resetFilters deps
    setSelectedDate(null);
    setSelectedCategory(null);
    setSelectedSubject(null);
    setSelectedAttribute(null);
    setSelectedDateRange(null); // Reset date range filter
    setSelectedReviewId(null); // ADDED: Reset selected review ID
    setSelectedSources(["all"]); // Reset source filter to show all
    // Clear selectedDateRange from localStorage when resetting
    localStorage.removeItem("selectedDateRange");
    // Do NOT reset timeframe here
    
    // REMOVED: fetchTableReviews call since ReviewsTable useEffect will handle the refetch
    // when the filter states change, preventing duplicate API calls
  }, [setSelectedReviewId, setSelectedDate, setSelectedCategory, setSelectedSubject, setSelectedAttribute, setSelectedDateRange]); // REMOVED: selectedBusinesses, timeframe from dependencies since we're not calling fetchTableReviews

  // --- Effect for saving settings to localStorage --- 
  useEffect(() => {
    localStorage.setItem("view", view);
  }, [view]);

  useEffect(() => {
    localStorage.setItem("sortOption", sortOption);
  }, [sortOption]);

  useEffect(() => {
    localStorage.setItem("timeframe", timeframe);
  }, [timeframe]);

  useEffect(() => {
    localStorage.setItem("selectedSources", JSON.stringify(selectedSources));
  }, [selectedSources]);

  useEffect(() => {
    // Only save date range to localStorage if it's truly a custom range (not calculated from "Last X Days")
    if (selectedDateRange && timeframe === "custom") {
      localStorage.setItem("selectedDateRange", JSON.stringify({
        start: selectedDateRange.start.toISOString(),
        end: selectedDateRange.end.toISOString()
      }));
      localStorage.setItem("isCustomDateRange", "true");
    } else if (timeframe !== "custom") {
      // Don't save calculated date ranges from standard timeframes
      localStorage.removeItem("selectedDateRange");
      localStorage.removeItem("isCustomDateRange");
    }
  }, [selectedDateRange, timeframe]);
  // --- End Effects for saving settings --- 

  // --- NEW: Fetcher for Paginated Reviews Table ---
  const fetchTableReviews = useCallback(async (fetchConfig) => {
    const {
      pageToFetch = 1,
      resetData = false,
      businessIdsToUse = (selectedBusinesses || []).map(b => b.id),
      timeframeToUse = timeframe,
      monthRangeToUse = selectedDateRange,
      categoryToUse = selectedCategory,
      subjectToUse = selectedSubject,
      reviewId = null, // ADDED: reviewId parameter
      sourcesToUse = selectedSources
    } = fetchConfig || {};

    // If a specific reviewId is provided, we don't need businessIdsToUse (backend handles authorization)
    // However, if businessIdsToUse is empty AND no reviewId, then clear.
    if ((!businessIdsToUse || businessIdsToUse.length === 0) && !reviewId) {
      setTableReviews([]);
      setTableReviewsPage(1);
      setTableReviewsHasMore(false);
      setIsTableReviewsLoading(false);
      setTableReviewsError(null);
      return;
    }

    setIsTableReviewsLoading(true);
    if (resetData) {
      setTableReviewsError(null);
    }

    const params = new URLSearchParams();
    
    if (reviewId) {
      params.append('review_id', reviewId);
      // If fetching a specific review, we might still need business_ids for the backend
      // to correctly associate user access, even if the primary filter is review_id.
      // The backend query for review_id implicitly checks user access via UserBusinessAssociation.
      // So, sending selected_business_ids might be redundant if review_id is present and backend handles it.
      // However, to be safe and match the endpoint's current flexible design:
      if (businessIdsToUse && businessIdsToUse.length > 0) {
        businessIdsToUse.forEach(id => params.append('selected_business_ids', id));
      }
    } else {
      // Apply business, timeframe, month range, category, subject filters ONLY if not fetching by reviewId
      businessIdsToUse.forEach(id => params.append('selected_business_ids', id));
      params.append('page', pageToFetch.toString());
      params.append('page_size', REVIEWS_TABLE_PAGE_SIZE.toString());

      if (monthRangeToUse && monthRangeToUse.start && monthRangeToUse.end) {
        params.append('start_date', monthRangeToUse.start.toISOString().split('T')[0]);
        params.append('end_date', monthRangeToUse.end.toISOString().split('T')[0]);
      } else if (timeframeToUse && timeframeToUse !== "all") {
        const parsedTimeframe = parseInt(timeframeToUse, 10);
        if (!isNaN(parsedTimeframe)) {
          const startDate = new Date(Date.now() - parsedTimeframe * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
          params.append('start_date', startDate);
        }
      }
      if (categoryToUse) params.append('category', categoryToUse);
      if (subjectToUse) params.append('subject_group', subjectToUse);
      // Add source filtering - only if not "all" and not empty
      if (sourcesToUse && sourcesToUse.length > 0 && !sourcesToUse.includes("all")) {
        sourcesToUse.forEach(source => params.append('source', source));
      }
    }

    try {
      const requestUrl = `${API_URL}/reviews/?${params.toString()}`;
      const response = await fetch(requestUrl, { method: 'GET', credentials: 'include' });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: `HTTP error! status: ${response.status}` }));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }
      const data = await response.json(); 
      
      if (resetData) {
        setTableReviews(data.reviews || []);
      } else {
        // Avoid adding duplicates if not resetting (though backend pagination should handle this)
        setTableReviews(prevReviews => {
          const newReviews = data.reviews || [];
          const existingReviewIds = new Set(prevReviews.map(r => r.id));
          const uniqueNewReviews = newReviews.filter(r => !existingReviewIds.has(r.id));
          return [...prevReviews, ...uniqueNewReviews];
        });
      }
      setTableReviewsPage(data.page);
      setTableReviewsHasMore(data.has_more);
      if (resetData) setTableReviewsError(null);

    } catch (error) {
      setTableReviewsError(error.message || "Failed to fetch reviews for table");
      if (resetData) {
        setTableReviews([]);
        setTableReviewsPage(1);
        setTableReviewsHasMore(true); 
      }
    } finally {
      setIsTableReviewsLoading(false);
    }
  }, [selectedBusinesses, timeframe, selectedDateRange, selectedCategory, selectedSubject, API_URL]);
  // --- END NEW: Fetcher for Paginated Reviews Table ---

  // --- Explicit Data Fetching Function ---
  const fetchData = useCallback(async (businessIdsToFetch, currentTf) => {
    // If no businesses are provided, clear data and stop
    if (!businessIdsToFetch || businessIdsToFetch.length === 0) {
        setReviewsData([]);
        setAnalysisData({});
        setIsReviewsLoading(false);
        setIsAnalysisLoading(false);
        setError(null); // Clear error if selection is cleared
        return;
    }
      
    setIsReviewsLoading(true);
    setIsAnalysisLoading(true);
    setError(null);

    // Build base query parameters including selected business IDs
    const baseParams = new URLSearchParams();
    businessIdsToFetch.forEach(id => baseParams.append('selected_business_ids', id));

    // Fetch Reviews
    const fetchReviews = async () => {
        let url = `${API_URL}/reviews/`;
        const params = new URLSearchParams(baseParams); // Clone base params
        // Apply timeframe filter
        if (currentTf !== "all") { 
            const parsedTimeframe = parseInt(currentTf, 10);
            if (!isNaN(parsedTimeframe)) {
                const startDate = new Date(Date.now() - parsedTimeframe * 24 * 60 * 60 * 1000).toISOString();
                params.set('start_date', startDate);
            }
        }
        // NOTE: Category/Subject filters are handled client-side for reviews in this context
          
        const paramString = params.toString();
        if (paramString) {
            url += `?${paramString}`;
        }

        const response = await fetch(url, { method: 'GET', credentials: 'include' });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
          
        const data = await response.json();
        return (data.reviews || []).sort((a, b) => {
            // Ensure date parsing is robust
            const dateA = a.estimated_datetime ? new Date(a.estimated_datetime) : new Date(0);
            const dateB = b.estimated_datetime ? new Date(b.estimated_datetime) : new Date(0);
            return dateB - dateA;
        });
    };

    // Fetch Analysis Data
    const fetchAnalysis = async () => {
      let url = `${API_URL}/analysis/`;
      const params = new URLSearchParams(baseParams); // Clone base params
      // NOTE: Add other analysis-specific filters if needed (e.g., timeframe?)
      // If timeframe should apply to analysis fetch too, add it here:
      if (currentTf !== "all") {
          const parsedTimeframe = parseInt(currentTf, 10);
          if (!isNaN(parsedTimeframe)) {
              const startDate = new Date(Date.now() - parsedTimeframe * 24 * 60 * 60 * 1000).toISOString();
              // Assuming analysis endpoint also accepts start_date
              params.set('start_date', startDate); 
              // If not, remove this part. If it uses a different param name, adjust accordingly.
              // For now, we assume analysis fetches ALL for the selected businesses
          }
      }
      const paramString = params.toString();
        if (paramString) {
            url += `?${paramString}`;
        }

      const response = await fetch(url, { method: 'GET', credentials: 'include' });

      if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    };

    try {
        const [fetchedReviews, fetchedAnalysis] = await Promise.all([
            fetchReviews(),
            fetchAnalysis()
        ]);
          
        setReviewsData(fetchedReviews);
        setIsReviewsLoading(false);

        // Process analysis data into map (remains the same)
        const analysisMap = (fetchedAnalysis || []).reduce((acc, analysis) => {
          const reviewId = analysis.review_id;
          if (!acc[reviewId]) {
            acc[reviewId] = [];
          }
          acc[reviewId].push(analysis);
          return acc;
        }, {});
        setAnalysisData(analysisMap);
        setIsAnalysisLoading(false);

    } catch (err) {
        setError(err.message || 'Failed to fetch data');
        setReviewsData([]);
        setAnalysisData({});
        setIsReviewsLoading(false);
        setIsAnalysisLoading(false);
    }
  }, []); // Empty dependency array: fetchData itself doesn't depend on changing props/state

  // --- NEW: Fetch aggregated dashboard data from backend ---
  const [aggregatedData, setAggregatedData] = useState(null);
  const [isAggregatedLoading, setIsAggregatedLoading] = useState(false);
  const [aggregatedError, setAggregatedError] = useState(null);

  // Separate state for different chart data to avoid unnecessary reloads
  const [subjectsChartData, setSubjectsChartData] = useState([]);
  const [attributesChartData, setAttributesChartData] = useState([]);
  const [isSubjectsChartLoading, setIsSubjectsChartLoading] = useState(false);
  const [isAttributesChartLoading, setIsAttributesChartLoading] = useState(false);

  const fetchAggregatedDashboardData = useCallback(async (businessIdsToFetch, currentTf, currentView, currentSortOption, currentSelectedCategory, currentSelectedSubject, currentSelectedAttribute, currentSelectedDateRange = null, currentSelectedSources = ["all"], options = {}) => {
    if (!businessIdsToFetch || businessIdsToFetch.length === 0) {
      setAggregatedData(null); // Clear data if no businesses
      setSubjectsChartData([]);
      setAttributesChartData([]);
      return;
    }

    const { 
      updateSubjectsChart = true, 
      updateAttributesChart = true, 
      updateOverview = true 
    } = options;

    setIsAggregatedLoading(true);
    if (updateSubjectsChart) setIsSubjectsChartLoading(true);
    if (updateAttributesChart) setIsAttributesChartLoading(true);
    setAggregatedError(null);

    const params = new URLSearchParams();
    businessIdsToFetch.forEach(id => params.append('selected_business_ids', id));

    // UPDATED: Check for custom date range first, then timeframe
    if (currentSelectedDateRange && currentSelectedDateRange.start && currentSelectedDateRange.end) {
      params.set('start_date', currentSelectedDateRange.start.toISOString().split('T')[0]);
      params.set('end_date', currentSelectedDateRange.end.toISOString().split('T')[0]);
    } else if (currentTf !== "all") {
      const parsedTimeframe = parseInt(currentTf, 10);
      if (!isNaN(parsedTimeframe)) {
        const startDate = new Date(Date.now() - parsedTimeframe * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        params.set('start_date', startDate);
        // Assuming end_date is implicitly today on the backend if not provided with start_date
      }
    }
    if (currentView) params.append('view', currentView);
    if (currentSortOption) params.append('sortOption', currentSortOption);
    if (currentSelectedCategory) params.append('selected_category_for_recs', currentSelectedCategory);
    if (currentSelectedSubject) params.append('selected_subject_for_attr_chart', currentSelectedSubject);
    if (currentSelectedAttribute) {
      params.append('selected_attribute_for_trends', currentSelectedAttribute);
      params.append('selected_attribute_for_subjects_chart', currentSelectedAttribute);
    }
    // Add source filtering - handle array properly
    if (currentSelectedSources && currentSelectedSources.length > 0 && !currentSelectedSources.includes("all")) {
      currentSelectedSources.forEach(source => params.append('source', source));
    }

    try {
      const requestUrl = `${API_URL}/dashboard-data/aggregated?${params.toString()}`;
      const response = await fetch(requestUrl, {
        method: 'GET',
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      
      if (updateOverview) {
        setAggregatedData(data);
      }
      if (updateSubjectsChart) {
        setSubjectsChartData(data.subjectsChartData || []);
      }
      if (updateAttributesChart) {
        setAttributesChartData(data.attributesChartData || []);
      }
    } catch (error) {
      setAggregatedError(error.message || "Failed to fetch aggregated data");
      if (updateOverview) setAggregatedData(null);
      if (updateSubjectsChart) setSubjectsChartData([]);
      if (updateAttributesChart) setAttributesChartData([]);
    } finally {
      setIsAggregatedLoading(false);
      if (updateSubjectsChart) setIsSubjectsChartLoading(false);
      if (updateAttributesChart) setIsAttributesChartLoading(false);
    }
  }, []); 

  // Optimized fetch functions for individual charts
  const fetchSubjectsChartData = useCallback(async (businessIdsToFetch, currentTf, currentView, currentSortOption, currentSelectedCategory, currentSelectedSources = ["all"]) => {
    return fetchAggregatedDashboardData(businessIdsToFetch, currentTf, currentView, currentSortOption, currentSelectedCategory, null, null, null, currentSelectedSources, {
      updateSubjectsChart: true,
      updateAttributesChart: false,
      updateOverview: false
    });
  }, [fetchAggregatedDashboardData]);

  const fetchAttributesChartData = useCallback(async (businessIdsToFetch, currentTf, currentView, currentSortOption, currentSelectedCategory, currentSelectedSubject, currentSelectedSources = ["all"]) => {
    return fetchAggregatedDashboardData(businessIdsToFetch, currentTf, currentView, currentSortOption, currentSelectedCategory, currentSelectedSubject, null, null, currentSelectedSources, {
      updateSubjectsChart: false,
      updateAttributesChart: true,
      updateOverview: false
    });
  }, [fetchAggregatedDashboardData]);

  // Function exposed to consumers to trigger a fetch
  const triggerRefetch = useCallback(() => {
    const businessIds = (selectedBusinesses || []).map(b => b.id);
    
    fetchTableReviews({
        pageToFetch: 1,
        resetData: true,
        businessIdsToUse: businessIds,
        timeframeToUse: timeframe,
        monthRangeToUse: selectedDateRange, 
        categoryToUse: selectedCategory,
        subjectToUse: selectedSubject,
        reviewId: selectedReviewId,
        sourcesToUse: selectedSources
    });

  }, [selectedBusinesses, timeframe, selectedDateRange, selectedCategory, selectedSubject, selectedReviewId, selectedSources]); // FIXED: Removed fetchTableReviews from dependencies to prevent circular dependency and duplicate calls

  // --- State and Fetcher for Paginated Comments Panel (Sentiment Separated) ---
  const [positiveCommentsPanelItems, setPositiveCommentsPanelItems] = useState([]);
  const [negativeCommentsPanelItems, setNegativeCommentsPanelItems] = useState([]);
  const [isCommentsPanelLoading, setIsCommentsPanelLoading] = useState(false);
  const [commentsPanelError, setCommentsPanelError] = useState(null);
  const [commentsPanelPage, setCommentsPanelPage] = useState(1); // Logical page for both sentiments
  const [commentsPanelHasMore, setCommentsPanelHasMore] = useState(true);
  const [commentsPanelTotalPositive, setCommentsPanelTotalPositive] = useState(0);
  const [commentsPanelTotalNegative, setCommentsPanelTotalNegative] = useState(0);

  // --- NEW: State for Overview Stats ---
  const [overviewStats, setOverviewStats] = useState(null);
  const [isOverviewLoading, setIsOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState(null);
  // --- End NEW: State for Overview Stats ---

  // --- NEW: State for Target Rating Data ---
  const [targetRatingData, setTargetRatingData] = useState(null);
  const [isTargetRatingLoading, setIsTargetRatingLoading] = useState(false);
  const [targetRatingError, setTargetRatingError] = useState(null);
  // --- End NEW: State for Target Rating Data ---

  // --- NEW: State for Cumulative Chart Data ---
  const [cumulativeChartData, setCumulativeChartData] = useState({ series_data: [], y_min: 0, y_max: 5 });
  const [isCumulativeChartLoading, setIsCumulativeChartLoading] = useState(false);
  const [cumulativeChartError, setCumulativeChartError] = useState(null);
  // --- End NEW: State for Cumulative Chart Data ---



  const fetchCommentsForPanel = useCallback(async (fetchConfig) => {
    const {
        pageToFetch = 1,
        businessIds,
        currentTf,
        currentSelectedCategory,
        currentSelectedSubject,
        currentSelectedAttribute,
        currentSelectedDate, // Single month Date object or null
        currentSelectedDateRange, // ADDED: Custom date range support
        currentSelectedSources = ["all"],
        resetData = false
    } = fetchConfig;

    if (!businessIds || businessIds.length === 0) {
      setPositiveCommentsPanelItems([]);
      setNegativeCommentsPanelItems([]);
      setCommentsPanelPage(1);
      setCommentsPanelHasMore(false);
      setCommentsPanelTotalPositive(0);
      setCommentsPanelTotalNegative(0);
      setCommentsPanelError(null);
      setIsCommentsPanelLoading(false);
      return;
    }

    setIsCommentsPanelLoading(true);
    if (resetData) {
        setCommentsPanelError(null);
    }

    const params = new URLSearchParams();
    businessIds.forEach(id => params.append('selected_business_ids', id));

    let startDateForAPI = null;
    let endDateForAPI = null;
    
    // UPDATED: Check for custom date range first, then single month, then timeframe
    if (currentSelectedDateRange && currentSelectedDateRange.start && currentSelectedDateRange.end) {
        startDateForAPI = currentSelectedDateRange.start.toISOString().split('T')[0];
        endDateForAPI = currentSelectedDateRange.end.toISOString().split('T')[0];
    } else if (currentSelectedDate instanceof Date && !isNaN(currentSelectedDate)) {
        startDateForAPI = new Date(currentSelectedDate.getFullYear(), currentSelectedDate.getMonth(), 1).toISOString().split('T')[0];
        endDateForAPI = new Date(currentSelectedDate.getFullYear(), currentSelectedDate.getMonth() + 1, 0).toISOString().split('T')[0];
    } else if (currentTf && currentTf !== "all") {
        const parsedTimeframe = parseInt(currentTf, 10);
        if (!isNaN(parsedTimeframe)) {
            startDateForAPI = new Date(Date.now() - parsedTimeframe * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        }
    }
    if (startDateForAPI) params.set('start_date', startDateForAPI);
    if (endDateForAPI) params.set('end_date', endDateForAPI);
    if (currentSelectedCategory) params.append('selected_category', currentSelectedCategory);
    if (currentSelectedSubject) params.append('selected_subject', currentSelectedSubject);
    if (currentSelectedAttribute) params.append('selected_attribute', currentSelectedAttribute);
    // Add source filtering - only if not "all" and not empty
    if (currentSelectedSources && currentSelectedSources.length > 0 && !currentSelectedSources.includes("all")) {
      currentSelectedSources.forEach(source => params.append('source', source));
    }
    
    params.append('page', pageToFetch.toString());
    params.append('items_per_sentiment_page', ITEMS_PER_SENTIMENT_PAGE_DEFAULT.toString());

    try {
      const requestUrl = `${API_URL}/comments/panel?${params.toString()}`;
      const response = await fetch(requestUrl, { method: 'GET', credentials: 'include' });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json(); // Expects PaginatedSentimentCommentsResponse
      
      if (resetData) {
        setPositiveCommentsPanelItems(data.positive_items || []);
        setNegativeCommentsPanelItems(data.negative_items || []);
      } else {
        setPositiveCommentsPanelItems(prevItems => [...prevItems, ...(data.positive_items || [])]);
        setNegativeCommentsPanelItems(prevItems => [...prevItems, ...(data.negative_items || [])]);
      }
      setCommentsPanelPage(data.page);
      setCommentsPanelHasMore(data.has_more);
      setCommentsPanelTotalPositive(data.total_positive_available || 0);
      setCommentsPanelTotalNegative(data.total_negative_available || 0);
      if (resetData) setCommentsPanelError(null);

    } catch (error) {
      setCommentsPanelError(error.message || "Failed to fetch comments");
      if (resetData) {
        setPositiveCommentsPanelItems([]);
        setNegativeCommentsPanelItems([]);
        setCommentsPanelPage(1);
        setCommentsPanelHasMore(true); 
        setCommentsPanelTotalPositive(0);
        setCommentsPanelTotalNegative(0);
      }
    } finally {
      setIsCommentsPanelLoading(false);
    }
  }, []); 
  // --- End State and Fetcher for Paginated Comments Panel ---

  // --- NEW: Fetcher for Overview Stats ---
  const fetchOverviewStats = useCallback(async (targetRating = null) => {
    if (!selectedBusinesses || selectedBusinesses.length === 0) {
      setOverviewStats(null);
      setIsOverviewLoading(false);
      setOverviewError(null);
      return;
    }

    setIsOverviewLoading(true);
    setOverviewError(null);

    const params = new URLSearchParams();
    selectedBusinesses.forEach(business => params.append('selected_business_ids', business.id));
    
    if (effectiveDateRange && effectiveDateRange.start && effectiveDateRange.end) {
      params.append('start_date', effectiveDateRange.start.toISOString().split('T')[0]);
      params.append('end_date', effectiveDateRange.end.toISOString().split('T')[0]);
    } else if (timeframe) {
      params.append('timeframe', timeframe);
    }

    if (view) params.append('view', view);
    if (selectedCategory) params.append('selected_category', selectedCategory);
    if (selectedSubject) params.append('selected_subject', selectedSubject);

    // Add target rating if provided
    if (targetRating && targetRating > 0) {
      params.append('target_rating', targetRating.toString());
    }

    // Add source filtering
    if (selectedSources && selectedSources.length > 0 && !selectedSources.includes("all")) {
      selectedSources.forEach(source => params.append('source', source));
    }

    try {
      const requestUrl = `${API_URL}/reviews/stats-overview/?${params.toString()}`;
      const response = await fetch(requestUrl, {
        method: 'GET',
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setOverviewStats(data);
    } catch (error) {
      setOverviewError(error.message || "Failed to fetch overview stats");
      setOverviewStats(null);
    } finally {
      setIsOverviewLoading(false);
    }
  }, [selectedBusinesses, timeframe, view, selectedCategory, selectedSubject, selectedDateRange, selectedSources]);

  // --- NEW: Fetcher for Target Rating Data ---
  const fetchTargetRatingData = useCallback(async (targetRating = null) => {
    if (!selectedBusinesses || selectedBusinesses.length === 0) {
      setTargetRatingData(null);
      setIsTargetRatingLoading(false);
      setTargetRatingError(null);
      return;
    }

    setIsTargetRatingLoading(true);
    setTargetRatingError(null);

    const params = new URLSearchParams();
    selectedBusinesses.forEach(business => params.append('selected_business_ids', business.id));
    
    // Add target rating if provided
    if (targetRating && targetRating > 0) {
      params.append('target_rating', targetRating.toString());
    }

    // Add source filtering
    if (selectedSources && selectedSources.length > 0 && !selectedSources.includes("all")) {
      selectedSources.forEach(source => params.append('source', source));
    }

    try {
      const requestUrl = `${API_URL}/reviews/target-rating/?${params.toString()}`;
      const response = await fetch(requestUrl, {
        method: 'GET',
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setTargetRatingData(data);
    } catch (error) {
      setTargetRatingError(error.message || "Failed to fetch target rating data");
      setTargetRatingData(null);
    } finally {
      setIsTargetRatingLoading(false);
    }
  }, [selectedBusinesses, selectedSources]);

  // --- NEW: Fetcher for Cumulative Chart Data ---
  const fetchCumulativeChartData = useCallback(async () => {
    if (!selectedBusinesses || selectedBusinesses.length === 0) {
      setCumulativeChartData({ series_data: [], y_min: 0, y_max: 5 }); 
      setIsCumulativeChartLoading(false);
      setCumulativeChartError(null);
      return;
    }

    setIsCumulativeChartLoading(true);
    setCumulativeChartError(null);

    const params = new URLSearchParams();
    selectedBusinesses.forEach(business => params.append('selected_business_ids', business.id));

    if (effectiveDateRange && effectiveDateRange.start && effectiveDateRange.end) {
      params.append('start_date', effectiveDateRange.start.toISOString().split('T')[0]);
      params.append('end_date', effectiveDateRange.end.toISOString().split('T')[0]);
    } else if (timeframe) {
      params.append('timeframe', timeframe);
    }

    // Add source filtering
    if (selectedSources && selectedSources.length > 0 && !selectedSources.includes("all")) {
      selectedSources.forEach(source => params.append('source', source));
    }

    try {
      const requestUrl = `${API_URL}/reviews/charts/cumulative-rating/?${params.toString()}`;
      const response = await fetch(requestUrl, { method: 'GET', credentials: 'include' });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json(); 
      setCumulativeChartData(data);
    } catch (error) {
      setCumulativeChartError(error.message || "Failed to fetch cumulative chart data");
      setCumulativeChartData({ series_data: [], y_min: 0, y_max: 5 }); 
    } finally {
      setIsCumulativeChartLoading(false);
    }
  }, [selectedBusinesses, timeframe, selectedDateRange, selectedSources]);



  const contextValue = useMemo(() => ({
    reviewsData,
    analysisData,
    isLoading: isReviewsLoading || isAnalysisLoading || isAggregatedLoading || isCommentsPanelLoading || isOverviewLoading || isTargetRatingLoading || isCumulativeChartLoading || isTableReviewsLoading || isSubjectsChartLoading || isAttributesChartLoading,
    isReviewsLoading,
    isAnalysisLoading,
    setIsLoading: (val) => { setIsReviewsLoading(val); setIsAnalysisLoading(val); }, 
    error, setError,
    aggregatedData,
    isAggregatedLoading,
    aggregatedError,
    fetchAggregatedDashboardData,
    subjectsChartData,
    attributesChartData,
    isSubjectsChartLoading,
    isAttributesChartLoading,
    fetchSubjectsChartData,
    fetchAttributesChartData,
    timeframe, setTimeframe, view, setView,
    selectedCategory, setSelectedCategory,
    selectedSubject, setSelectedSubject,
    selectedAttribute, setSelectedAttribute,
    selectedDate, setSelectedDate,
    selectedDateRange, setSelectedDateRange,
    effectiveDateRange,
    selectedReviewId, setSelectedReviewId,
    showSentimentExplanation, setShowSentimentExplanation,
    showTopicExplanation, setShowTopicExplanation,
    sortOption, setSortOption,
    selectedSources, setSelectedSources,
    resetFilters,
    triggerRefetch, 
    positiveCommentsPanelItems,
    negativeCommentsPanelItems,
    isCommentsPanelLoading,
    commentsPanelError,
    commentsPanelPage,
    commentsPanelHasMore,
    commentsPanelTotalPositive,
    commentsPanelTotalNegative,
    fetchCommentsForPanel,
    overviewStats,
    isOverviewLoading,
    overviewError,
    fetchOverviewStats,
    targetRatingData,
    isTargetRatingLoading,
    targetRatingError,
    fetchTargetRatingData,
    cumulativeChartData,
    isCumulativeChartLoading,
    cumulativeChartError,
    fetchCumulativeChartData, 
    tableReviews,
    tableReviewsPage,
    tableReviewsHasMore,
    isTableReviewsLoading,
    tableReviewsError,
    fetchTableReviews, 
    REVIEWS_TABLE_PAGE_SIZE 
  }), [
    reviewsData,
    analysisData, isReviewsLoading,
    isAnalysisLoading, error, timeframe, view, selectedCategory, 
    selectedSubject, selectedAttribute, selectedDate, selectedDateRange, effectiveDateRange, selectedReviewId,
    showSentimentExplanation, showTopicExplanation, sortOption, selectedSources, triggerRefetch, resetFilters,
    aggregatedData, isAggregatedLoading, aggregatedError, fetchAggregatedDashboardData,
    subjectsChartData, attributesChartData, isSubjectsChartLoading, isAttributesChartLoading, fetchSubjectsChartData, fetchAttributesChartData,
    positiveCommentsPanelItems, negativeCommentsPanelItems, isCommentsPanelLoading, commentsPanelError, commentsPanelPage, commentsPanelHasMore, commentsPanelTotalPositive, commentsPanelTotalNegative, fetchCommentsForPanel,
    overviewStats, isOverviewLoading, overviewError, fetchOverviewStats,
    cumulativeChartData, isCumulativeChartLoading, cumulativeChartError, fetchCumulativeChartData, 

    tableReviews, tableReviewsPage, tableReviewsHasMore, isTableReviewsLoading, tableReviewsError, fetchTableReviews,
    setSelectedCategory, setSelectedSubject, setSelectedAttribute, setSelectedDate, setSelectedDateRange, setSelectedReviewId, setTimeframe, setView, setSortOption, setSelectedSources, setShowSentimentExplanation, setShowTopicExplanation, setError
  ]);

  return (
    <ReviewsContext.Provider value={contextValue}>
      {children}
    </ReviewsContext.Provider>
  );

}
